import {
  TYPED_DATE_TIME_FIELDS,
  TYPED_DATE_TIME_PLACEHOLDER,
  formatTypedDateTime,
  parseTypedDateTime,
} from './typed-date.mjs?v=20260806.100';
import { columnEditableForRole, containerNumberEditable } from './container-field-registry.mjs?v=20260806.100';
import { containerStatusPresentation } from './container-status-presentation.mjs?v=20260806.100';

function escapeHtml(value) {
  return String(value ?? '').replaceAll('&', '&amp;').replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#039;');
}

/**
 * Elides the middle of a long identifier: `COSU6509135240` -> `COSU…35240`.
 *
 * The middle and not the tail. Alan asked for the leading letters with the rest
 * on hover, and measured against the 497 MBLs in production that would have
 * drawn "COSU" on 150 rows — the column would have cost the same glance and
 * answered nothing. Keeping the last five digits leaves 4 rows in 497 sharing a
 * rendering instead of 469, for one extra character.
 *
 * Returns the value unchanged when it is short enough that eliding would save
 * nothing, so a 9-character MBL is never drawn as `COSU…35240`.
 */
export function abbreviateIdentifier(value, abbreviate) {
  const text = String(value ?? '');
  if (!abbreviate) return text;
  const head = Number(abbreviate.head) || 0;
  const tail = Number(abbreviate.tail) || 0;
  if (head < 1 || tail < 1 || text.length <= head + tail + 1) return text;
  return `${text.slice(0, head)}…${text.slice(-tail)}`;
}

function statusToken(value) {
  const normalized = String(value || '').trim().toLowerCase();
  // customer_paid is a value of the fee itself now, not a flag beside it
  // (Alan, 2026-08-02), so it is a token like the other two.
  if (normalized === 'customer_paid') return 'customer_paid';
  return normalized === 'paid' ? 'paid' : normalized === 'unpaid' ? 'unpaid' : null;
}

// A fee with no recorded state is unpaid, not unknown (Alan, 2026-08-05). Every
// Container owes CTF and TMF from the moment it exists, and the backend already
// stores "unpaid" on creation - so a blank here is a Container whose finance
// summary has not been written yet, never a fee that is somehow exempt. It used
// to draw grey, which reads as "nothing to collect".
function feeToken(value) {
  return statusToken(value) ?? 'unpaid';
}

export function ctfTmfTone(ctf, tmf) {
  const values = [feeToken(ctf), feeToken(tmf)];
  if (values.every((value) => value === 'paid')) return 'success';
  if (values.every((value) => value === 'unpaid')) return 'danger';
  return 'warning';
}

/**
 * Per-chip colour: green paid, light blue Customer Paid, red otherwise. Each fee
 * answers for itself - CTF can be Customer Paid while TMF is unpaid (Alan,
 * 2026-08-02) - so the colour comes from this fee's own value and never from a
 * flag shared with the other one.
 */
export function ctfChipTone(value) {
  const token = feeToken(value);
  if (token === 'customer_paid') return 'customer';
  return token === 'paid' ? 'success' : 'danger';
}

/**
 * One control, one colour. Customer Paid outranks the CTF/TMF pair because it is
 * the state the finance desk acts on; otherwise the pair decides, and a mixed
 * pair stays amber rather than claiming either end.
 */
export function ctfPaymentTone({ ctf, tmf, customerPaid } = {}) {
  if (customerPaid === true) return 'customer';
  return ctfTmfTone(ctf, tmf);
}

export function ctfPaymentLabel({ ctf, tmf, customerPaid } = {}) {
  const readable = (value) => {
    const token = feeToken(value);
    return token === 'customer_paid' ? 'Customer Paid' : token[0].toUpperCase() + token.slice(1);
  };
  const pair = `CTF ${readable(ctf)}, TMF ${readable(tmf)}`;
  return customerPaid === true ? `Customer Paid — ${pair}` : pair;
}

export function compactLocationLabel(value) {
  const normalized = String(value || '').trim().replace(/\s+/g, ' ');
  if (!normalized) return '—';
  const parts = normalized.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 3) return `${parts.at(-2)}, ${parts.at(-1)}`;
  return normalized.length > 30 ? `${normalized.slice(0, 28)}…` : normalized;
}

function fieldButtonAttributes(column, row, suffix = '') {
  return `data-open-cell-editor data-container-id="${escapeHtml(row.id)}" data-field="${escapeHtml(column.key)}${suffix}"`;
}

function valueSlug(value) {
  return String(value || 'neutral').toLowerCase().replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '') || 'neutral';
}

function ctfTmfValues(row, formatted) {
  const fallback = String(formatted || '').split('/').map((value) => statusToken(value));
  const fallbackCustomer = String(formatted || '').split('/')[2]?.trim().toLowerCase();
  return {
    ctf: statusToken(row.ctf_status) || fallback[0] || null,
    tmf: statusToken(row.tmf_status) || fallback[1] || null,
    customerPaid: row.customer_paid === true || fallbackCustomer === 'customer_paid',
  };
}

export function canOpenPodEditor({ role, editMode } = {}) {
  // The clerk adds POD too (Alan, 2026-09-25); canEditDashboardPod says the same.
  return editMode === true && ['admin', 'dispatcher', 'clerk'].includes(String(role || '').trim().toLowerCase());
}

const CALENDAR_ICON = '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M5 2.5v3M15 2.5v3M3 7h14M4 4h12a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z"/></svg>';
// The location pin went with the map popup: the cell is text now.

export function dashboardCellContent({
  column,
  row,
  role,
  formatted,
  editMode,
  locale = globalThis.document?.documentElement?.lang || 'en',
}) {
  if (!column || !row) return '';
  const editable = editMode === true && columnEditableForRole(column, role)
    && !(column.key === 'container_no' && !containerNumberEditable(row, role));
  const display = String(formatted ?? '—');
  if (column.editor === 'ctf_tmf') {
    const { ctf, tmf, customerPaid } = ctfTmfValues(row, display);
    // The CTF and TMF labels stay on screen; what disappeared is the Paid /
    // Unpaid wording, which colour now carries — green paid, red unpaid, brown
    // Customer Paid (Alan, 2026-07-31). Recorded trade-off: colour-only state is
    // not distinguishable to red-green colour blind operators, so the accessible
    // name and the tooltip still spell it out.
    const label = ctfPaymentLabel({ ctf, tmf, customerPaid });
    const chip = (field, text, value) => {
      // This fee's own state decides its colour and its accessible name. The shared
      // customer_paid flag used to override both chips at once, which is what made
      // the pair unable to disagree (Alan, 2026-08-02).
      const tone = ctfChipTone(value);
      const token = statusToken(value) ?? 'unpaid';
      const readableValue = token === 'customer_paid'
        ? 'Customer Paid'
        : token[0].toUpperCase() + token.slice(1);
      const stateLabel = `${text} ${readableValue}`;
      return editable
        ? `<button type="button" class="dashboard-ctf-chip dashboard-ctf-chip--${tone}" ${fieldButtonAttributes({ key: field }, row)} aria-label="${escapeHtml(stateLabel)}" title="${escapeHtml(stateLabel)}">${escapeHtml(text)}</button>`
        : `<span class="dashboard-ctf-chip dashboard-ctf-chip--${tone}" aria-label="${escapeHtml(stateLabel)}" title="${escapeHtml(stateLabel)}">${escapeHtml(text)}</span>`;
    };
    return `<span class="dashboard-ctf-tmf" role="group" aria-label="${escapeHtml(label)}">${chip('ctf_status', 'CTF', ctf)}${chip('tmf_status', 'TMF', tmf)}</span>`;
  }
  if (column.interaction === 'note') {
    const text = String(row[column.key] ?? '').trim();
    // The note is read, not opened: it is always in the cell, clipped to one
    // line so row height never varies, with the full multi-line text on hover.
    // A native title attribute is not usable here — it collapses the newline in
    // "CUSTOMS HOLD / LINE HOLD" and drops the second line.
    if (!text) {
      return editable
        ? `<button type="button" class="dashboard-note-cell dashboard-note-cell--empty" ${fieldButtonAttributes(column, row)} aria-label="Add ${escapeHtml(column.label)}">—</button>`
        : '<span class="dashboard-note-cell dashboard-note-cell--empty">—</span>';
    }
    // The customer's note is a log, newest line on top (composeAppendOnlyNote). Folded to its newest line
    // and a "+N" for the rest (Alan, 2026-10-11: "最新消息永远显示在上面，其他折叠一下吧，太长了"); every line
    // is still in the hover, newest first.
    const lines = FOLDED_NOTE_FIELDS.has(column.key) ? text.split(/\n+/).map((line) => line.trim()).filter(Boolean) : [];
    const shown = lines.length ? lines[0] : text;
    const older = lines.length > 1 ? `<span class="dashboard-note-cell__more" aria-hidden="true">+${lines.length - 1}</span>` : '';
    const inner = `<span class="dashboard-note-cell__text">${escapeHtml(shown)}</span>${older}<span class="dashboard-note-cell__full" role="tooltip">${escapeHtml(text)}</span>`;
    return editable
      ? `<button type="button" class="dashboard-note-cell has-note" ${fieldButtonAttributes(column, row)} aria-label="${escapeHtml(`${column.label}: ${text}`)}">${inner}</button>`
      : `<span class="dashboard-note-cell has-note" tabindex="0" aria-label="${escapeHtml(`${column.label}: ${text}`)}">${inner}</span>`;
  }
  if (column.interaction === 'address') {
    // No map (Alan, 2026-08-10: "删除地图的tab...mouse over 就会显示地址全部,
    // 不会再显示花里胡哨的了"). The cell shows the short form, hovering shows the
    // whole address, and that is the entire interaction.
    //
    // The map popup was also the only way to EDIT the three text address columns
    // - empty_from, transfer_from, transfer_to - because its onUpdate wrote the
    // field. Deleting it without this would have quietly made them read-only, so
    // an editable address cell becomes an ordinary editor trigger instead.
    const value = String(row[column.key] ?? '').trim();
    const short = compactLocationLabel(value);
    if (!value) {
      return editable
        ? `<button type="button" class="dashboard-location-button" ${fieldButtonAttributes(column, row)} aria-label="Add ${escapeHtml(column.label)}">—</button>`
        : '<span class="dashboard-location-button">—</span>';
    }
    // An empty that was reported and then moved on the way (Alan, 2026-10-11: "已经报空的中途又转仓的在这个地方
    // 标记上"). The team writes that into Empty From by hand - "10/11带回611" - and nothing else records it (no
    // reported Container on production carries a Transfer leg), so the cell's own words are the signal: a text that
    // says 转仓 / 带回 gets a 转仓 tag and a tint, and stays the plain editable text it was.
    const moved = column.key === 'empty_from_address' && EMPTY_MOVED_WORDS.test(value);
    const pill = moved ? '<span class="dashboard-transfer-pill" aria-hidden="true">转仓</span>' : '';
    const inner = `${pill}<span class="dashboard-abbr__short">${escapeHtml(short)}</span>`
      + `<span class="dashboard-abbr__full dashboard-abbr__full--address" role="tooltip">${escapeHtml(value)}</span>`;
    const label = escapeHtml(`${column.label}: ${value}`);
    const kind = moved ? 'dashboard-location-button dashboard-location-button--moved' : 'dashboard-location-button';
    return editable
      ? `<button type="button" class="${kind}" data-abbreviated="true" ${fieldButtonAttributes(column, row)} aria-label="${label}">${inner}</button>`
      : `<span class="${kind}" data-abbreviated="true" tabindex="0" aria-label="${label}">${inner}</span>`;
  }
  if (column.interaction === 'pod') {
    const count = Number(row[column.key] || 0);
    const available = count > 0;
    const editablePod = canOpenPodEditor({ role, editMode });
    const interaction = editablePod
      ? `data-open-pod-editor data-container-id="${escapeHtml(row.id)}" aria-label="Add or edit POD photos"`
      : available
      ? `data-open-pod data-container-id="${escapeHtml(row.id)}" aria-label="Open POD photos"`
      : 'disabled aria-label="No POD photos available"';
    // One icon, not a seven-element paper stack. The folder used to be assembled per row from a
    // back panel, three sheets and two front flaps, each with its own shadow — 2,870 elements
    // across 410 Containers, and the single largest cost in rendering the table. It is the reason
    // the interface felt slow, and the reason a density pass went after the product's shadows and
    // rounding instead (Alan, 2026-07-31).
    return `<button type="button" class="dashboard-pod-folder ${available ? 'dashboard-pod-folder--filled' : 'dashboard-pod-folder--empty'}" ${interaction}><i class="ph ${available ? 'ph-folder' : 'ph-folder-dashed'}" aria-hidden="true"></i></button>`;
  }
  if (column.interaction === 'copy') {
    const value = row[column.key] ?? '';
    const short = abbreviateIdentifier(display, column.abbreviate);
    // Only the drawing shrinks. The copy target, the accessible name and the
    // editor all still carry the whole value, so nothing downstream ever sees
    // the ellipsis.
    const body = short === display
      ? escapeHtml(display)
      : `<span class="dashboard-abbr__short">${escapeHtml(short)}</span>`
        + `<span class="dashboard-abbr__full" role="tooltip">${escapeHtml(display)}</span>`;
    const abbreviated = short === display ? '' : ' data-abbreviated="true"';
    // An editable identifier carries BOTH hooks. Cells are always built as their
    // editable selves - that is what makes the lock instant - so without
    // data-copy-value here a Container # was an editor trigger and nothing else,
    // and locked it did nothing at all: the editor refuses, and the click had
    // nowhere else to go. Click-to-copy was dead on every column the role can
    // edit (Alan, 2026-08-10: "这个导致我设置的点击copy失效了"). Which of the two
    // fires is the click handler's decision, taken against the lock at the time
    // of the click.
    const primary = editable
      ? `<button type="button" class="dashboard-copy-value dashboard-cell-value--editable"${abbreviated} ${fieldButtonAttributes(column, row)} data-copy-value="${escapeHtml(value)}" aria-label="${escapeHtml(`${column.label}: ${display}`)}">${body}</button>`
      : `<button type="button" class="dashboard-copy-value"${abbreviated} data-copy-value="${escapeHtml(value)}" aria-label="Copy ${escapeHtml(column.label)}: ${escapeHtml(display)}">${body}</button>`;
    return `<span class="dashboard-identifier-value">${primary}</span>`;
  }
  if (column.editor === 'date') {
    return `<span class="dashboard-date-value"><span>${escapeHtml(display)}</span>${editable ? `<button type="button" class="dashboard-calendar-button" ${fieldButtonAttributes(column, row)} aria-label="Edit ${escapeHtml(column.label)}">${CALENDAR_ICON}</button>` : ''}</span>`;
  }
  if (column.editor === 'driver') {
    return editable
      ? `<button type="button" class="dashboard-cell-value dashboard-cell-value--editable dashboard-driver-trigger" ${fieldButtonAttributes(column, row)} aria-haspopup="listbox" aria-expanded="false">${escapeHtml(display)}</button>`
      : `<span class="dashboard-cell-value">${escapeHtml(display)}</span>`;
  }
  if (column.key === 'status' || column.key === 'ocean_status') {
    const presentation = containerStatusPresentation(row[column.key], locale);
    const statusClass = `container-status container-status--${presentation.tone} container-status--${presentation.token}`;
    return editable
      ? `<button type="button" class="${statusClass} dashboard-cell-value--editable" ${fieldButtonAttributes(column, row)}>${escapeHtml(presentation.label)}</button>`
      : `<span class="${statusClass}">${escapeHtml(presentation.label)}</span>`;
  }
  const expenseFields = new Set([
    'return_cost_cents', 'pickup_cost_cents', 'transfer_cost_cents',
    'exception_fee_cents', 'special_fee_amount_cents',
    'special_fee_2_amount_cents', 'special_fee_3_amount_cents',
    'total_expense_cents', 'pier_pass_cents',
  ]);
  // Customer Receivable is money the customer actually pays us, so it reads as
  // revenue alongside the two fees (Alan, 2026-08-03).
  const customerRevenueFields = new Set([
    'customer_special_fee_cents', 'service_fee_cents', 'customer_receivable_cents',
  ]);
  // Profit is green only once the money is in. Remittance is the whole rule: Not
  // Received keeps it red however large the number is, Received turns it green
  // (Alan, 2026-08-03). Those two are the only inputs - the amount itself does not
  // colour it, because a profit nobody has paid yet is not a profit.
  const semanticClass = column.key === 'profit_cents'
    ? (String(row.remittance_status ?? '') === 'received'
      ? ' dashboard-cell-value--profit'
      : ' dashboard-cell-value--special-fee')
    : customerRevenueFields.has(column.key)
      ? ' dashboard-cell-value--customer-revenue'
      : expenseFields.has(column.key)
        ? ' dashboard-cell-value--special-fee' : '';
  if (column.chip) {
    const chipClass = `adaptive-data-chip adaptive-data-chip--${valueSlug(display)}`;
    return editable
      ? `<button type="button" class="${chipClass} dashboard-cell-value--editable${semanticClass}" ${fieldButtonAttributes(column, row)}>${escapeHtml(display)}</button>`
      : `<span class="${chipClass}${semanticClass}">${escapeHtml(display)}</span>`;
  }
  // The value itself, as an attribute, so a column can be coloured by what it
  // says without every such rule needing its own branch in here. Container Type
  // 45 is the first user (Alan, 2026-08-11: "只要是45的全部标记成紫色").
  const valueHook = ` data-cell-value="${escapeHtml(valueSlug(display))}"`;
  if (editable) {
    return `<button type="button" class="dashboard-cell-value dashboard-cell-value--editable${semanticClass}"${valueHook} ${fieldButtonAttributes(column, row)}>${escapeHtml(display)}</button>`;
  }
  return `<span class="dashboard-cell-value${semanticClass}"${valueHook}>${escapeHtml(display)}</span>`;
}

export function dashboardEditorValue(column, rawValue) {
  if (!column || typeof column !== 'object') throw new TypeError('Dashboard editor column is invalid.');
  if (column.key === 'customer_paid') {
    if (rawValue !== true && String(rawValue || '').trim() !== 'customer_paid') {
      throw new TypeError('Confirm Customer Paid.');
    }
    return true;
  }
  if (column.editor === 'actual_empty_return') {
    const value = rawValue && typeof rawValue === 'object' ? rawValue : {};
    const date = String(value.date || '').trim();
    const time = String(value.time || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || (time && !/^\d{2}:\d{2}$/.test(time))) {
      throw new TypeError('Enter a valid actual return date and optional time.');
    }
    return { date, time };
  }
  // A cleared editor clears the field (Alan, 2026-08-16: "表格里面的信息只能改不能
  // 删除，应该可以删除的"). Until this date a blank date threw "Enter a valid
  // date." and a blank number became ZERO - so clearing Weight silently stored
  // 0 lbs, which is a value, not an absence. Null is what the server stores for
  // a cleared date, number or amount; text clears to "" further down.
  const blank = rawValue === null || rawValue === undefined
    || (typeof rawValue === 'string' && rawValue.trim() === '');
  if (blank && column.editor !== 'actual_empty_return') {
    return column.editor === 'date' || column.editor === 'currency' || column.editor === 'number' ? null : '';
  }
  // Empty # is typed again (2026-09-27). Stored the way the handoff and every
  // lookup match a number - upper case, no spaces - so "tgbu 1234567" names the
  // same box as TGBU1234567.
  if (column.key === 'empty_container_no') {
    return String(rawValue).trim().toUpperCase().replace(/\s+/g, '');
  }
  if (column.editor === 'date') {
    if (column.format === 'date-only') {
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(rawValue || '').trim());
      if (!match) throw new TypeError('Enter a valid date.');
      const [, year, month, day] = match;
      const millis = Date.UTC(Number(year), Number(month) - 1, Number(day), 12);
      const calendarDate = new Date(millis);
      if (calendarDate.getUTCFullYear() !== Number(year)
        || calendarDate.getUTCMonth() !== Number(month) - 1
        || calendarDate.getUTCDate() !== Number(day)) {
        throw new TypeError('Enter a valid date.');
      }
      return millis;
    }
    // What the native picker sends, or what someone typed (Pickup Appt is typed
    // since 2026-09-26). `new Date("9/28 14:00")` would read that as the year 2001,
    // so typed text is parsed here rather than handed to Date.
    const text = String(rawValue || '').trim();
    const local = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(text)
      ? text : parseTypedDateTime(text, Date.now(), { timeOptional: TYPED_DATE_TIME_FIELDS.has(column?.key) });
    const millis = local ? new Date(local).getTime() : NaN;
    if (!Number.isSafeInteger(millis) || millis < 0) {
      throw new TypeError('请输入日期（时间可空），如 9/28 或 9/28 14:00 · Enter a date, with or without a time: 9/28 or 9/28 14:00');
    }
    return millis;
  }
  if (column.editor === 'currency') {
    const dollars = Number(String(rawValue ?? '').trim());
    if (!Number.isFinite(dollars) || dollars < 0) throw new TypeError('Enter a valid amount.');
    return Math.round(dollars * 100);
  }
  if (column.editor === 'number') {
    const numeric = Number(String(rawValue ?? '').trim());
    if (!Number.isFinite(numeric) || numeric < 0) throw new TypeError('Enter a valid number.');
    return numeric;
  }
  return String(rawValue ?? '').trim();
}

export function dashboardDateTimeInputValue(value) {
  if (!(value instanceof Date) || !Number.isFinite(value.getTime())) return '';
  const local = new Date(value.getTime() - value.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

export function dashboardDateInputValue(value) {
  if (!(value instanceof Date) || !Number.isFinite(value.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(value);
  const values = Object.fromEntries(parts.map(({ type, value: part }) => [type, part]));
  return `${values.year}-${values.month}-${values.day}`;
}

// Words that say an empty was moved after it was reported. Exported so the table's tint and a test read one list.
export const EMPTY_MOVED_WORDS = /转仓|转场|转运|带回/u;

// Notes that are a log rather than a single text: only the newest line shows in the cell.
// The price list's terms fold the same way: first line shown, the rest in the hover.
const FOLDED_NOTE_FIELDS = new Set(['customer_note', 'customer_quote_terms']);

export function dashboardEditorInputSpec(column, value) {
  if (column?.editor !== 'date') throw new TypeError('Dashboard date editor column is invalid.');
  if (TYPED_DATE_TIME_FIELDS.has(column.key)) {
    return { type: 'text', value: formatTypedDateTime(value, { omitMidnight: true }), placeholder: TYPED_DATE_TIME_PLACEHOLDER };
  }
  return column.format === 'date-only'
    ? { type: 'date', value: dashboardDateInputValue(value) }
    : { type: 'datetime-local', value: dashboardDateTimeInputValue(value) };
}
